import { ReactionBlock } from "./Miscs.js"

export type Interaction = {
    interactionId: string,
    coid: number|null,                         // only when in Op-context, after it has been saved to the backend
    userId:number,                          // only when in Op-context, will be overridden by backend
    userName:string|null,                        // only when in Op-context, will be overridden by backend
    usergroupId: string,
    interactionType: InteractionType,
    anchor: interactionAnchor,
    style: interactionStyle,
    label: string,
    reactions: ReactionBlock,
    tCreated: number,
    tModified: number,
    tSeen?:number,
    dSeen?:number,
}

export type interactionAnchor = {
    nodeId: string,
    relText: string,
    rects: Rect[],
    tool: InteractionToolLiteral
}

export type interactionStyle = {
    color?: string | null
}

export const interactionTypePluralLiterals = ['annotations', 'emotions', 'comments', 'links', 'weblinks', 'taggings', 'readingQuestions'] as const;
export type InteractionTypePlural = (typeof interactionTypePluralLiterals)[number];
export const typeGuard_InteractionTypePlural = function (input:any): input is InteractionType {
    try {
        return (interactionTypePluralLiterals.includes(input))
    } catch (error) {
        throw error;
    }
}

export const interactionTypeLiterals = ['annotation', 'emotion', 'comment', 'link', 'weblink', 'tagging', 'readingQuestion', 'message'] as const;
export type InteractionType = (typeof interactionTypeLiterals)[number];
/**
 *
 * @param input
 * @returns {boolean}
 */
export const typeGuard_InteractionType = function (input:any): input is InteractionType {
    try {
        return (interactionTypeLiterals.includes(input))
    } catch (error) {
        throw error;
    }
}

export const interactionToolLiterals = ['box', 'marker','pencil','highlighter'] as const;
export type InteractionToolLiteral = (typeof interactionToolLiterals)[number];
export const typeGuard_InteractionToolLiteral = function (input:any): input is InteractionToolLiteral {
    try {
        return (interactionToolLiterals.includes(input))
    } catch (error) {
        throw error;
    }
}

export type iAnnotation = {
    interactionType: 'annotation'
} & Interaction

export type iAnnotationFingerprint = {
    interactionId: string,
    coid: number|null,
    anchor: interactionAnchor,
    label: string,
    tCreated: number,
    tModified: number,
}

export type iEmotion = {
    interactionType: 'emotion' | 'annotation',
    emotionId: string, // will be utf-8 identifier in the future
} & Interaction

export type iEmotionFingerprint = {
    interactionId: string,
    coid: number|null,
    anchor: interactionAnchor,
    label: string,
    tCreated: number,
    tModified: number,
    emotionId: string
}

export type iComment = {
    interactionType: 'comment' | 'annotation',
    commentType?: string,                       // replace by keyed 0 => comment, 1 => readingQuestion, etc. enum   -------> or move to a general "qualifier"-string in the Interaction Base type? (valid for tags, too?)
} & Interaction

export type iCommentFingerprint = {
    interactionId: string,
    coid: number|null,
    anchor: interactionAnchor,
    label: string,
    tCreated: number,
    tModified: number,
}

export type iReadingQuestion = {
    interactionType: 'readingQuestion' | 'annotation',
    openUntil:number|null,
    answerVisibility: 'public'|'instructor'|'afterReply',
    answerVisibilityDelay: number|null,
} & Interaction

export type iReadingQuestionFingerprint = {
    interactionId: string,
    coid: number|null,
    anchor: interactionAnchor,
    label: string,
    tCreated: number,
    tModified: number,
}

export type iLink = {
    interactionType: 'link' | 'annotation',
    linkId: string,
    linkType: iLinkLinkType,                         // replace by keyed 0 => related, 1 => nextPage, etc. enum
    which: iLinkWhich,
    other?: iLink | string | null,
} & Interaction

export type iLinkFingerprint = {
    interactionId: string,
    coid: number|null,
    anchor: interactionAnchor,
    label: string,
    tCreated: number,
    tModified: number,
    linkId: string
}

const iLinkLinkTypeLiterals = ['related', 'nextPage'] as const;
export type iLinkLinkType  = (typeof iLinkLinkTypeLiterals)[number];
export const typeGuard_iLinkLinkType = function (input:any): input is iLinkLinkType {
    try {
        return (iLinkLinkTypeLiterals.includes(input))
    } catch (error) {
        throw error;
    }
}

const iLinkWhichLiterals = ['src', 'dst'] as const;
export type iLinkWhich = (typeof iLinkWhichLiterals)[number];
export const typeGuard_iLinkWhich = function (input:any): input is iLinkWhich {
    try {
        return (iLinkWhichLiterals.includes(input));
    } catch (error) {
        throw error;
    }
}

export type iWeblink = {
    interactionType: 'weblink' | 'annotation',
    url: string,
} & Interaction

export type iWeblinkFingerprint = {
    interactionId: string,
    coid: number|null,
    anchor: interactionAnchor,
    label: string,
    tCreated: number,
    tModified: number,
    url: string
}

export type iTag = {
    interactionType: 'tagging' | 'annotation',
    tagId: string,
} & Interaction

export type iTagFingerprint = {
    interactionId: string,
    coid: number|null,
    anchor: interactionAnchor,
    label: string,
    tCreated: number,
    tModified: number,
    tagId: string
}

export type iLoc = {
    contentType:'pdfFile',
    nodeId:string,
    interactionType: InteractionType,
}

export type Rect = {
    x: number,
    y: number,
    w: number,
    h: number,
    p: number,
    svg?:string,
}
